/**
 * AdminModerationPage — super_admin / admin view of all roundtable cluster
 * messages with one-click delete and an audit trail. Lives under the LMS
 * sidebar (admin-only).
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trash, ShieldWarning, ChatCircleDots, ClockClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const CLUSTERS = [
  "All", "General", "Proprietors", "School Owners", "Principals", "Vice Principals",
  "Head Teachers", "Teachers", "Subject Specialists", "Administrators", "ICT Personnel",
  "Finance", "HR", "Marketing & Admissions", "Auxiliary Services", "Welfare", "Operations",
];

export default function AdminModerationPage() {
  const [messages, setMessages] = useState([]);
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const loadMessages = () => {
    setLoading(true);
    const q = filter && filter !== "All" ? `?roundtable=${encodeURIComponent(filter)}` : "";
    api.get(`/pod/moderation/messages${q}`)
      .then(r => setMessages(r.data || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  };

  const loadLogs = () => {
    api.get("/pod/moderation/log").then(r => setLogs(r.data || [])).catch(() => setLogs([]));
  };

  useEffect(() => { loadMessages(); }, [filter]); // eslint-disable-line
  useEffect(() => { loadLogs(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this message? This action is logged.")) return;
    try {
      await api.delete(`/pod/moderation/messages/${id}`);
      toast.success("Message deleted");
      loadMessages();
      loadLogs();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-10 max-w-6xl mx-auto" data-testid="admin-moderation-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-600 font-bold">Moderation</p>
            <h1 className="text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Community Oversight</h1>
            <p className="text-sm text-slate-600 mt-1">Monitor cluster chats across the POD. All deletes are recorded in the audit log.</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56 rounded-none" data-testid="moderation-cluster-filter">
              <SelectValue placeholder="Filter by cluster" />
            </SelectTrigger>
            <SelectContent>
              {CLUSTERS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="messages">
          <TabsList className="rounded-none">
            <TabsTrigger value="messages" className="rounded-none gap-2" data-testid="moderation-tab-messages"><ChatCircleDots size={14} /> Messages</TabsTrigger>
            <TabsTrigger value="log" className="rounded-none gap-2" data-testid="moderation-tab-log"><ClockClockwise size={14} /> Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="messages" className="mt-4">
            {loading && <p className="text-sm text-slate-500">Loading…</p>}
            {!loading && messages.length === 0 && (
              <Card className="rounded-none"><CardContent className="p-8 text-center">
                <ShieldWarning size={32} className="mx-auto text-slate-400" />
                <p className="text-sm text-slate-600 mt-2">No messages match this filter.</p>
              </CardContent></Card>
            )}
            <div className="space-y-2">
              {messages.map(m => (
                <Card key={m.id} className="rounded-none border border-slate-200" data-testid={`mod-msg-${m.id}`}>
                  <CardContent className="p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-slate-900 text-amber-400 rounded-none text-[10px] uppercase">{m.roundtable}</Badge>
                        <p className="text-xs font-semibold text-slate-950">{m.author_name || "Unknown"}</p>
                        {m.author_email && <p className="text-[10px] text-slate-500 truncate">{m.author_email}</p>}
                        <p className="text-[10px] text-slate-400 ml-auto whitespace-nowrap">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</p>
                      </div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{m.text}</p>
                    </div>
                    <button onClick={() => remove(m.id)} className="shrink-0 text-red-600 hover:text-red-800 p-1" data-testid={`mod-delete-${m.id}`} title="Delete (audited)">
                      <Trash size={16} />
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="log" className="mt-4">
            {logs.length === 0 && <p className="text-sm text-slate-500">No moderation actions yet.</p>}
            <div className="space-y-1">
              {logs.map(l => (
                <div key={l.id} className="text-xs flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-none" data-testid={`mod-log-${l.id}`}>
                  <Badge className="bg-red-700 text-white rounded-none text-[10px]">{l.action}</Badge>
                  <span className="text-slate-600">target <code className="bg-slate-100 px-1">{l.target_id}</code></span>
                  <span className="text-slate-500">by <strong>{l.moderator_name}</strong></span>
                  <span className="ml-auto text-slate-400">{new Date(l.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
